import type { QuickBooksFpaStatementRender } from '../../../data/ServerApiClient'
import { formatAsOf } from './cfoFormat'

export type CfoStatementComparisonMode =
  | 'prior_month'
  | 'prior_quarter'
  | 'prior_year'
  | 'ytd'
  | 'rolling_12'
  | 'rolling_quarters'
  | 'rolling_years'
  | 'rolling_3_vs_prior_3'
  | 'rolling_12_vs_prior_12'

export type CfoStatementColumnSpec = {
  key: string
  label: string
  kind: 'value' | 'delta' | 'pct'
  period?: string | null
  periodIndex?: number
  indices?: number[]
  currentIndices?: number[]
  priorIndices?: number[]
}

const periodKey = (period: string | null | undefined) => {
  const match = /^(\d{4})-(\d{2})/.exec(String(period || ''))
  return match ? `${match[1]}-${match[2]}` : ''
}

const monthOffset = (period: string | null | undefined, offset: number) => {
  const key = periodKey(period)
  if (!key) return ''
  const [yearRaw, monthRaw] = key.split('-').map(Number)
  const date = new Date(Date.UTC(yearRaw, monthRaw - 1 + offset, 1))
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

const formatMonthHeader = (period: string | null | undefined) =>
  formatAsOf(period).replace(/\s+\d{4}$/, '').slice(0, 3).toUpperCase()

const formatQuarterHeader = (period: string | null | undefined) => {
  const key = periodKey(period)
  if (!key) return 'Q'
  const [yearRaw, monthRaw] = key.split('-').map(Number)
  return `Q${Math.floor((monthRaw - 1) / 3) + 1} ${yearRaw}`
}

const formatYearHeader = (period: string | null | undefined) => {
  const key = periodKey(period)
  return key ? key.slice(0, 4) : 'Year'
}

const periodBucketKey = (period: string | null | undefined, bucket: 'quarter' | 'year') => {
  const key = periodKey(period)
  if (!key) return ''
  const [yearRaw, monthRaw] = key.split('-').map(Number)
  if (bucket === 'year') return String(yearRaw)
  return `${yearRaw}-Q${Math.floor((monthRaw - 1) / 3) + 1}`
}

export const buildCfoStatementColumnSpecs = (
  statement: Pick<QuickBooksFpaStatementRender, 'periods'>,
  endPeriod: string | null | undefined,
  comparisonMode: CfoStatementComparisonMode,
  maxPeriods: number,
): CfoStatementColumnSpec[] => {
  const periods = statement.periods
  if (!periods.length) return []
  const indexByKey = new Map(periods.map((period, index) => [periodKey(period), index]))
  const fallbackIndex = Math.max(0, periods.length - 1)
  const selectedIndex = indexByKey.get(periodKey(endPeriod)) ?? fallbackIndex
  const selectedPeriod = periods[selectedIndex]
  const value = (key: string, label: string, indices: number[], periodIndex?: number): CfoStatementColumnSpec => ({
    key,
    label,
    kind: 'value',
    period: indices.length === 1 ? periods[indices[0]] || null : null,
    periodIndex,
    indices,
  })
  const comparison = (key: string, label: string, currentIndices: number[], priorIndices: number[], kind: 'delta' | 'pct'): CfoStatementColumnSpec => ({
    key,
    label,
    kind,
    period: currentIndices.length === 1 ? periods[currentIndices[0]] || null : null,
    currentIndices,
    priorIndices,
  })
  const comparativeColumns = (key: string, priorLabel: string, currentLabel: string, priorIndices: number[], currentIndices: number[]) => [
    value(`${key}:prior`, priorLabel, priorIndices, priorIndices.length === 1 ? priorIndices[0] : undefined),
    value(`${key}:current`, currentLabel, currentIndices, currentIndices.length === 1 ? currentIndices[0] : undefined),
    comparison(`${key}:delta`, 'Change', currentIndices, priorIndices, 'delta'),
    comparison(`${key}:pct`, 'Change %', currentIndices, priorIndices, 'pct'),
  ]

  if (comparisonMode === 'rolling_12') {
    const start = Math.max(0, selectedIndex - Math.max(1, maxPeriods) + 1)
    return periods.slice(start, selectedIndex + 1).map((period, offset) => {
      const index = start + offset
      return value(`period:${periodKey(period)}:${index}`, formatMonthHeader(period), [index], index)
    })
  }

  if (comparisonMode === 'rolling_quarters' || comparisonMode === 'rolling_years') {
    const bucketKind = comparisonMode === 'rolling_quarters' ? 'quarter' : 'year'
    const buckets: Array<{ key: string; label: string; indices: number[] }> = []
    periods.forEach((period, index) => {
      if (index > selectedIndex) return
      const key = periodBucketKey(period, bucketKind)
      if (!key) return
      const previous = buckets[buckets.length - 1]
      if (previous?.key === key) previous.indices.push(index)
      else buckets.push({
        key,
        label: bucketKind === 'quarter' ? formatQuarterHeader(period) : formatYearHeader(period),
        indices: [index],
      })
    })
    return buckets
      .slice(-Math.max(1, maxPeriods))
      .map((bucket) => value(`${bucketKind}:${bucket.key}`, bucket.label, bucket.indices))
  }

  if (comparisonMode === 'rolling_12_vs_prior_12') {
    const currentStart = Math.max(0, selectedIndex - 11)
    const currentIndices = Array.from({ length: selectedIndex - currentStart + 1 }, (_, index) => currentStart + index)
    const priorStart = Math.max(0, currentStart - 12)
    const priorIndices = Array.from({ length: currentStart - priorStart }, (_, index) => priorStart + index)
    return comparativeColumns('rolling12', 'Prior 12', 'Last 12', priorIndices, currentIndices)
  }

  if (comparisonMode === 'rolling_3_vs_prior_3') {
    const currentStart = Math.max(0, selectedIndex - 2)
    const currentIndices = Array.from({ length: selectedIndex - currentStart + 1 }, (_, index) => currentStart + index)
    const priorStart = Math.max(0, currentStart - 3)
    const priorIndices = Array.from({ length: currentStart - priorStart }, (_, index) => priorStart + index)
    return comparativeColumns('rolling3', 'Prior 3', 'Last 3', priorIndices, currentIndices)
  }

  if (comparisonMode === 'ytd') {
    const selectedKey = periodKey(selectedPeriod)
    const [yearRaw, monthRaw] = selectedKey.split('-').map(Number)
    const currentIndices = periods
      .map((period, index) => ({ key: periodKey(period), index }))
      .filter(({ key }) => key >= `${yearRaw}-01` && key <= `${yearRaw}-${String(monthRaw).padStart(2, '0')}`)
      .map(({ index }) => index)
    const priorYear = yearRaw - 1
    const priorIndices = periods
      .map((period, index) => ({ key: periodKey(period), index }))
      .filter(({ key }) => key >= `${priorYear}-01` && key <= `${priorYear}-${String(monthRaw).padStart(2, '0')}`)
      .map(({ index }) => index)
    return comparativeColumns('ytd', `YTD ${priorYear}`, `YTD ${yearRaw}`, priorIndices, currentIndices)
  }

  const priorIndex = comparisonMode === 'prior_year'
    ? indexByKey.get(monthOffset(selectedPeriod, -12)) ?? -1
    : comparisonMode === 'prior_quarter'
      ? selectedIndex - 3
      : selectedIndex - 1
  const priorLabel = comparisonMode === 'prior_year'
    ? formatAsOf(periods[priorIndex] || monthOffset(selectedPeriod, -12))
    : comparisonMode === 'prior_quarter'
      ? periods[priorIndex] ? formatAsOf(periods[priorIndex]).replace(/\s+\d{4}$/, '') : 'Prior quarter'
      : periods[priorIndex] ? formatAsOf(periods[priorIndex]).replace(/\s+\d{4}$/, '') : 'Prior'
  const currentLabel = comparisonMode === 'prior_year'
    ? formatAsOf(selectedPeriod)
    : formatAsOf(selectedPeriod).replace(/\s+\d{4}$/, '')
  return comparativeColumns('period', priorLabel, currentLabel, priorIndex >= 0 ? [priorIndex] : [], [selectedIndex])
}
